import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

/* The site name is the row's real control: the surrounding row is clickable for
   the mouse, but a plain div/tr with role="button" would make its children
   presentational and hide the metric cells from screen readers. So keyboard and
   assistive tech get a genuine <button> here, and the row keeps its semantics.

   No onClick of its own — activating a native button fires a click that bubbles
   to the row's handler, so there is exactly one navigation path. */

const nameLayout = `
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
  max-width: 100%;
`;

const NameButton = styled.button`
  ${nameLayout}
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #1a73e8;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const NameStatic = styled.span`
  ${nameLayout}
`;

const NameText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Persistent but faint at rest so a wall of grey names still reads as
// navigable; the row's hover brightens it.
const Caret = styled.span`
  flex-shrink: 0;
  opacity: 0.4;
  font-size: 1.05em;
  line-height: 1;
`;

const OrgSiteName = ({ name, navigable, label }) => {
  if (!navigable) {
    return (
      <NameStatic>
        <NameText className="org-site-name">{name}</NameText>
      </NameStatic>
    );
  }

  return (
    <NameButton type="button" aria-label={label || name}>
      <NameText className="org-site-name">{name}</NameText>
      <Caret className="org-site-caret" aria-hidden>›</Caret>
    </NameButton>
  );
};

OrgSiteName.propTypes = {
  name: PropTypes.string,
  navigable: PropTypes.bool,
  // Overrides the accessible name when the row carries extra context (e.g. a
  // breakdown), so screen reader users get what sighted users get on hover.
  label: PropTypes.string
};

OrgSiteName.defaultProps = {
  name: '',
  navigable: false,
  label: null
};

export default OrgSiteName;
